import { Fragment, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ACCOUNT_PERMISSION } from '../../shared/constants'
import type {
	Conflict,
	Reservation,
	Proposal,
	ReservationWorkflowRequest,
} from '../../shared/types'
import { useHasAccountPermission } from '../account-permissions'
import { useObjectList } from '../api/object'
import { useReservationListConflicts } from '../api/reservations'
import { ButtonLink } from '../components/button'
import { ClientFormatter } from '../components/client-formatter'
import { DateFormatter } from '../components/date-formatter'
import { LocationFormatter } from '../components/location-formatter'
import { Paper } from '../components/paper'
import { ProposalVehicleFormatter } from '../components/proposal-vehicle-formatter'
import { SourceFormatter } from '../components/source-formatter'
import { VehicleFormatter } from '../components/vehicle-formatter'
import { useIgnoreRentner } from '../ignore-rentner'
import { REQUEST_TYPE_LABEL } from '../reservations/const'
import { STATUS as PROP_STATUS, STATUS_STYLES as PROP_STYLES } from '../proposals/const'
import { STATUS, STATUS_STYLES } from '../reservations/const'
import { List, useList } from '../scaffolding/list'

export function Overview() {
	return (
		<>
			<PendingWorkflowApprovals />
			<Conflicts />
			<div className="grid lg:grid-cols-2 gap-8 items-start">
				<div className="lg:col-span-2">
					<Reservations title="Rezervace s přijatým příjmem" status="paid" />
				</div>
				<Reservations title="Nové Rezervace" status="new" />
				<Proposals title="Nové Poptávky" status="new" />
			</div>
		</>
	)
}

function PendingWorkflowApprovals() {
	const canConfirmReservation = useHasAccountPermission(ACCOUNT_PERMISSION.CONFIRM_RESERVATION)
	const { data, isLoading } = useObjectList(
		'reservation-workflow-request',
		{
			status: 'pending',
			page: 1,
			orderKey: 'id',
			orderDir: 'DESC',
		},
		{ enabled: canConfirmReservation },
	)
	const pendingRequests = (data?.list ?? []) as unknown as ReservationWorkflowRequest[]
	const total = data?.total ?? 0
	const visibleItems = pendingRequests.slice(0, 6)

	if (!canConfirmReservation) {
		return null
	}

	if (isLoading) {
		return (
			<section className="mb-10">
				<div className="bg-amber-50 rounded-xl border border-amber-300 px-5 py-4 text-sm text-amber-700">
					Načítám požadavky ke schválení...
				</div>
			</section>
		)
	}

	if (!total) {
		return null
	}

	return (
		<section className="mb-10">
			<div className="bg-amber-50 rounded-xl border border-amber-300 overflow-hidden">
				<div className="flex items-center justify-between gap-3 px-5 py-4 sm:px-6 sm:py-5">
					<div>
						<h3 className="text-md font-bold text-amber-900">Čeká na schválení</h3>
						<p className="mt-1 text-sm text-amber-700">
							ATD výdej/vratka čeká na rozhodnutí.
						</p>
					</div>
					<span className="rounded-full bg-amber-200 px-3 py-1 text-xs font-semibold text-amber-900">
						{total}
					</span>
				</div>

				<div className="overflow-x-auto border-t border-amber-200">
					<table className="w-full">
						<thead>
							<tr>
								<th className="bg-amber-100">Rezervace</th>
								<th className="bg-amber-100">Typ</th>
								<th className="bg-amber-100">Podáno</th>
								<th className="bg-amber-100"></th>
							</tr>
						</thead>
						<tbody>
							{visibleItems.map(request => (
								<tr key={request.id}>
									<td width="25%">
										<Link to={`/svc/reservation/${request.reservation_id}`}>
											#{request.reservation_id}
										</Link>
									</td>
									<td width="25%">{REQUEST_TYPE_LABEL[request.request_type]}</td>
									<td width="30%">
										{request.created_at ? (
											<DateFormatter
												date={request.created_at}
												format="d.M. HH:mm"
											/>
										) : (
											'neuvedeno'
										)}
									</td>
									<td width="20%">
										<ButtonLink
											variant="TEXT"
											size="SMALL"
											to={`/svc/reservation/${request.reservation_id}`}
										>
											Otevřít rezervaci
										</ButtonLink>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				{total > visibleItems.length ? (
					<div className="border-t border-amber-200 px-5 py-3 text-xs text-amber-700">
						Zobrazeno {visibleItems.length} z {total} čekajících požadavků.
					</div>
				) : null}
			</div>
		</section>
	)
}

function Conflicts() {
	const query = useReservationListConflicts()
	const [params, setParams] = useList()

	return (
		<section className="mb-10">
			<div className="bg-rose-50 rounded-xl border border-rose-400">
				<div className="px-5 py-4 sm:px-6 sm:py-5">
					<h3 className="text-md font-bold">Varování</h3>
				</div>
				<List<Conflict>
					params={params}
					setParams={setParams}
					query={query}
					header={
						<tr>
							<th className="bg-rose-200">Vozidlo</th>
							<th className="bg-rose-200">Termín</th>
							<th className="bg-rose-200"></th>
						</tr>
					}
				>
					{conflict => (
						<Fragment key={conflict.id}>
							<tr className="border-b-0 border-t-2">
								<td width="30%">
									<Link to={`/svc/vehicle/${conflict.vehicle_id}`}>
										<VehicleFormatter id={conflict.vehicle_id} />
									</Link>
								</td>
								<td width="48%">
									<DateFormatter date={conflict.date_from} format="d.M. HH:mm" />
									{' - '}
									<DateFormatter date={conflict.date_to} format="d.M. HH:mm" />
								</td>
								<td width="22%">
									<ButtonLink
										variant="TEXT"
										size="SMALL"
										to={`/svc/reservation/${conflict.id}`}
									>
										Detail
									</ButtonLink>
								</td>
							</tr>
							{conflict.conflict_with?.map(other => (
								<tr key={other.id} className="border-b-0">
									<td width="30%"></td>
									<td width="48%">
										<DateFormatter date={other.date_from} format="d.M. HH:mm" />
										{' - '}
										<DateFormatter date={other.date_to} format="d.M. HH:mm" />
									</td>
									<td width="22%">
										<ButtonLink
											variant="TEXT"
											size="SMALL"
											to={`/svc/reservation/${other.id}`}
										>
											Detail
										</ButtonLink>
									</td>
								</tr>
							))}
						</Fragment>
					)}
				</List>
			</div>
		</section>
	)
}

function Reservations({ status, title }: { status: string; title: ReactNode }) {
	const [params, setParams] = useList()
	const ignoreRentner = useIgnoreRentner()

	const query = useObjectList('reservation', { ...params, status: [status], ignoreRentner })

	return (
		<Paper>
			<div className="px-5 py-4 sm:px-6 sm:py-5">
				<h3 className="text-md font-bold">{title}</h3>
			</div>
			<List<Reservation>
				params={params}
				setParams={setParams}
				query={query}
				header={
					<tr>
						<th>Vozidlo</th>
						<th>Termín</th>
						<th>Pobočka</th>
						<th>Klient</th>
						<th>Zdroj</th>
					</tr>
				}
			>
				{reservation => (
					<tr key={reservation.id}>
						<td width="25%">
							<Link to={`/svc/reservation/${reservation.id}`}>
								<VehicleFormatter id={reservation.vehicle_id} />
							</Link>
						</td>
						<td width="20%">
							<DateFormatter date={reservation.date_from} format="d.M. HH:mm" />
							<br />
							<DateFormatter date={reservation.date_to} format="d.M. HH:mm" />
						</td>
						<td width="25%">
							<Link to={`/svc/locations/${reservation.location_from}`}>
								<LocationFormatter id={reservation.location_from} />
							</Link>
						</td>
						<td width="25%">
							<Link to={`/svc/client/${reservation.client_id}`}>
								<span className="block mb-1">
									<ClientFormatter id={reservation.client_id} />
								</span>
								<span
									className={`px-3 py-1 rounded-xl text-xs font-semibold ${STATUS_STYLES[reservation.status]}`}
								>
									{STATUS[reservation.status]}
								</span>
							</Link>
						</td>
						<td width="5%">
							<SourceFormatter source={reservation.source} />
						</td>
					</tr>
				)}
			</List>
		</Paper>
	)
}

function Proposals({ status, title }: { status: string; title: ReactNode }) {
	const [params, setParams] = useList()

	const query = useObjectList('proposal', {
		...params,
		status: [status],
	})

	return (
		<Paper>
			<div className="px-5 py-4 sm:px-6 sm:py-5">
				<h3 className="text-md font-bold">{title}</h3>
			</div>

			<List<Proposal>
				params={params}
				setParams={setParams}
				query={query}
				header={
					<tr>
						<th>Poptané vozidlo</th>
						<th>Termín</th>
						<th>Pobočka</th>
						<th>Klient</th>
						<th>Zdroj</th>
					</tr>
				}
			>
				{proposal => (
					<tr key={proposal.id}>
						<td width="25%">
							<Link to={`/svc/proposal/${proposal.id}`}>
								<ProposalVehicleFormatter id={proposal.proposal_vehicle_id} />
							</Link>
						</td>

						<td width="20%">
							<DateFormatter date={proposal.date_from} format="d.M. HH:mm" />
							<br />
							<DateFormatter date={proposal.date_to} format="d.M. HH:mm" />
						</td>

						<td width="20%">
							<LocationFormatter id={proposal.location_from} />
							<br />
							<LocationFormatter id={proposal.location_to} />
						</td>

						<td width="25%">
							<Link to={`/svc/client/${proposal.client_id}`}>
								<span className="block mb-1">
									<ClientFormatter id={proposal.client_id} />
								</span>

								<span
									className={`px-3 py-1 rounded-xl text-xs font-semibold ${PROP_STYLES[proposal.status]}`}
								>
									{PROP_STATUS[proposal.status]}
								</span>
							</Link>
						</td>

						<td width="10%">
							<SourceFormatter source={proposal.source} />
						</td>
					</tr>
				)}
			</List>
		</Paper>
	)
}
